import { useEffect, useMemo, useState, useRef } from "react";
import { motion } from "framer-motion";
import ContactForm from "../pages/ContactForm";

const MENU = [
    {
        name: "South Indian Roti",
        items: [
            "Akki Roti","Masala Akki Roti","Plain Akki Roti","Ragi Roti","Jolada Roti",
            "Kadak Jolada Roti","Sajje Roti","Mangalore Dry Roti","Rumali Roti",
            "Menthya Rumali Roti","Wheat Rumali Roti","Tandoor Roti","Veg. Stuffed Parota",
            "Potato Parota","Pulka","Channa Batora","Tandoori Nan","Butter Nan",
            "Stuffed Nan","Garlic Nan","Cheese Nan",
        ],
    },
    {
        name: "South Indian Curry",
        items: [
            "Veg Sagu","Bombay Sagu","Samebadhnekai Sagu","M.T.R. Sagu","Pitik Poppu",
            "Batani Kootu","Perch Kootu","Hirekai Kootu","Alu+Brinjal Nati Kootu",
            "Kadalekalu Kootu","Avarekalu Kootu","Alasandekalu Kootu","Huralikalu Kootu",
            "Hesarukalu Kootu",
        ],
    },
    {
        name: "Bajji",
        items: [
            "Ridge Gourd Bajji","Banana Bajji","Chilli Bajji","Alu Bajji","Big Chilli Bajji",
            "Onion Bajji","Veg Bonda","Alu Bonda","Maddur Vada","Rava Vada","Masala Vada",
            "Ambode","Pakoda","Rava Pakoda","Pudi Pakoda","Mandalore Bajji","Curd Vada",
            "Masala Curd Vada","Betel Leaves Bajji","Paneer Pakoda","Masala Chilli Bajji",
        ],
    },
    {
        name: "Rice Bath Items",
        items: [
            "Veg Palav","Ghee Rice","Veg Dhum Biryani","Menthya Bath","Bisibele Bath",
            "Peas Palav","Veg Fried Rice","Mushroom Fried Rice","Paneer Fried Rice",
            "Mushroom Biryani","Paneer Palav","Alu Biryani","Jeera Rice","Bread Palav",
            "Hyderabadi Biryani","Mogalai Biryani","Navaratna Palav","Dhum Vangibath",
            "Kalasida Vangibath","Lemon Ricebath","Veg Lemon Ricebath",
            "Capsicum Peas Lemon Rice Bath","Pudeena Rice Bath","Puliyogare",
            "Coconut Rice Bath","Curry Leaves Bath","Shavige Bath",
            "Bread Mix Shavigi Vangibath","Chinese Noodles",
        ],
    },
    {
        name: "North Indian Curries",
        items: [
            "Paneer Butter Masala","Palak Paneer Masala","Shahi Paneer","Chenna Masala",
            "Malai Koftha","Kadai Paneer","Baby Corn Masala","Khaju Masala",
            "Mushroom Masala","Bhendi Masala","Alu Ghobi","Alu Chenna","Alu Matter",
            "Dhum Alu","Veg Shahi Kurma","Mix Vegetables Curry",
            "Green Peas Masala","Green Peas Capsicum Masala",
        ],
    },
];

export default function MenuSelection() {
    const [selected, setSelected] = useState([]);
    const [showWarning, setShowWarning] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const contactRef = useRef(null);

    useEffect(() => {
        const saved = localStorage.getItem("bookingBasics");
        if (saved) setPopupData(JSON.parse(saved));
        window.scrollTo(0, 0);
    }, []);

    const toggleDish = (dish) => {
        setSelected((prev) =>
            prev.includes(dish)
                ? prev.filter((d) => d !== dish)
                : [...prev, dish]
        );
    };

    const totalSelected = useMemo(() => selected.length, [selected]);

    const handleProceed = () => {
        if (totalSelected < 10) {
            setShowWarning(true);
            setTimeout(() => setShowWarning(false), 2500);
        }
        contactRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleClear = () => setSelected([]);
    const handleBack = () => (window.location.href = "/");

    return (
        <div className="pt-24 bg-white text-[#1F2937] min-h-screen">
            {/* Top Summary Bar */}
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 text-center md:text-left">
                <motion.h1
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-3xl md:text-4xl font-serif text-[#d4af37] mb-3"
                >
                    Craft Your Menu
                </motion.h1>

                {popupData && (
                    <div className="text-[#4B5563] italic mb-2">
                        Occasion:{" "}
                        <span className="text-[#d4af37] font-semibold">{popupData.occasion}</span>{" "}
                        | Service Type:{" "}
                        <span className="text-[#d4af37] font-semibold">{popupData.service}</span>{" "}
                        | Number of People:{" "}
                        <span className="text-[#d4af37] font-semibold">{popupData.numPeople}</span>
                    </div>
                )}

                <p className="text-[#4B5563]">
                    Choose your favorite dishes below (minimum 10 recommended).
                </p>

                <p className="text-[#6B7280] italic mt-3">
                    Don’t want to choose dishes?{" "}
                    <button
                        onClick={() =>
                            contactRef.current?.scrollIntoView({ behavior: "smooth" })
                        }
                        className="text-[#d4af37] underline-offset-4 hover:underline hover:text-[#f2d98b] transition"
                    >
                        Scroll down to leave your info
                    </button>{" "}
                    — we’ll curate the perfect menu for you.
                </p>
            </div>

            {/* Warning Toast */}
            {showWarning && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-[#d4af37] text-white font-semibold px-6 py-3 rounded-full shadow-lg z-50"
                >
                    Please select at least 10 dishes before proceeding!
                </motion.div>
            )}

            {/* Menu Categories */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32">
                {MENU.map((cat, idx) => (
                    <div key={idx} className="mb-8">
                        <h2 className="text-2xl text-[#d4af37] font-serif mb-3 border-b border-[#d4af37]/40 pb-2">
                            {cat.name}{" "}
                            <span className="text-sm text-[#6B7280] ml-2">
                                ({cat.items.filter((i) => selected.includes(i)).length} selected)
                            </span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {cat.items.map((dish) => {
                                const isSelected = selected.includes(dish);
                                return (
                                    <button
                                        key={dish}
                                        onClick={() => toggleDish(dish)}
                                        className={`rounded-lg border px-4 py-3 text-left transition-all ${
                                            isSelected
                                                ? "bg-[#d4af37] text-white font-semibold border-[#d4af37]"
                                                : "bg-[#F9FAFB] text-[#1F2937] border-[#d4af37]/30 hover:border-[#d4af37]/70"
                                        }`}
                                    >
                                        {dish}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </section>

            {/* Bottom Sticky Bar */}
            <div className="fixed bottom-0 left-0 w-full bg-white/95 border-t border-[#d4af37]/30 backdrop-blur-lg py-3 z-40 shadow-lg">
                <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center gap-4 text-sm md:text-base">
                    <button
                        onClick={handleProceed}
                        className="px-6 py-2 rounded-full bg-gradient-to-r from-[#d4af37] to-[#b88a2d] text-white font-semibold hover:scale-[1.03] transition"
                    >
                        Leave Contact Info →
                    </button>
                    <button
                        onClick={handleClear}
                        className="px-6 py-2 rounded-full border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/20 transition"
                    >
                        Clear Selection
                    </button>
                    <button
                        onClick={handleBack}
                        className="px-6 py-2 rounded-full border border-[#d4af37]/60 text-[#d4af37]/80 hover:bg-[#d4af37]/20 transition"
                    >
                        ← Go Back
                    </button>
                </div>
            </div>

            {/* Contact Form Footer */}
            <div ref={contactRef}>
                <ContactForm selectedDishes={selected} />
            </div>
        </div>
    );
}
